import React from 'react';

export function withContext<CP>(
  Context: React.Context<CP>,
  shouldRender: (context: CP) => boolean = () => true,
) {
  return function wrapComponent<P extends CP, T = unknown>(
    WrappedComponent: React.ComponentType<React.PropsWithChildren<P>>,
  ) {
    type WrappedComponentProps = Omit<React.PropsWithChildren<P>, keyof CP>;
    type WrappedComponentPropsWithForwardRef = WrappedComponentProps & {
      forwardRef: React.Ref<T>;
    };

    const InjectContext: React.FC<React.PropsWithChildren<WrappedComponentPropsWithForwardRef>> = (
      props,
    ) => {
      const { forwardRef, ...rest } = props;
      let refProp = {};

      if (WrappedComponent?.prototype?.isReactComponent) {
        refProp = { ref: forwardRef };
      } else {
        refProp = { forwardRef };
      }

      return (
        <Context.Consumer>
          {(context) =>
            shouldRender(context) ? (
              <WrappedComponent {...refProp} {...(rest as any)} {...context} />
            ) : null
          }
        </Context.Consumer>
      );
    };

    return React.forwardRef<T, WrappedComponentProps>((props, ref) => (
      <InjectContext forwardRef={ref} {...props} />
    ));
  };
}

export default withContext;
